/**
 * Vitrina de intrare.
 *
 * Se intra DIRECT in restaurante. Adresa e o bara in pagina, nu o poarta: pana la
 * varianta asta, primul ecran era un formular de noua campuri, iar lista de
 * restaurante statea sub el, dezactivata.
 *
 * ADR-003 nu cere ordinea aceea. El cere ca VANZATORUL sa fie cunoscut inainte de
 * a contracta, iar asta se intampla la finalizare, cu cosul complet. Rasfoirea
 * meniului inainte de adresa nu incalca nimic — nu se contracteaza nimic
 * rasfoind.
 *
 * CE se afiseaza si IN CE ORDINE vine din blocurile pieței
 * (`config.layout.blocks`), compuse in builderul din Hub. Pagina asta ramane cu
 * trei lucruri pe care un bloc nu le poate avea: fereastra de adresa (una
 * singura pentru toate blocurile care o cer), legatura cu cosul si — pentru cine
 * a mai comandat de pe dispozitivul asta — cardul „Comanda din nou", cel mai
 * frecvent motiv de revenire pe o platforma de livrare.
 */

import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { HOME_PAGE_SLUG } from "@deliveryapp/shared";

import { BlockStack, resolvePageBlocks, type BlockContext } from "../blocks";
import { useBootstrap } from "../components/BootstrapProvider";
import { formatAddress } from "../components/AddressForm";
import { useAddressSheet } from "../components/AddressSheetProvider";
import { cartLinesFromRemembered, readRememberedOrders, useCart } from "../components/CartProvider";
import { IconChevronRight, IconRepeat } from "../components/icons";
import { useToast } from "../components/ToastProvider";
import { Button, Price } from "../components/ui";

export function Home() {
  const { bootstrap, config, marketKey, t } = useBootstrap();
  const { session, selectStorefront, restoreOrder } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const addressSheet = useAddressSheet();

  const blocks = useMemo(() => resolvePageBlocks(config, HOME_PAGE_SLUG), [config]);

  // La o singura vitrina nu exista nimic de ales: dupa adresa intram direct in
  // meniu, unde se verifica zona, taxa si bucataria. Pietele cu restaurante
  // diferite raman pe lista, ca utilizatorul sa aleaga — iar cardurile spun
  // care dintre ele livreaza la el.
  const requestAddress = useCallback(() => {
    addressSheet.open({
      onSaved: () => {
        const only = bootstrap.storefronts.length === 1 ? bootstrap.storefronts[0] : undefined;
        if (only === undefined) return;
        selectStorefront(only.slug, only.name);
        navigate(`/vitrina/${encodeURIComponent(only.slug)}`);
      },
    });
  }, [addressSheet, bootstrap.storefronts, selectStorefront, navigate]);

  // Ultima comanda cu produse retinute, plasata de pe dispozitivul asta, pe o
  // vitrina care inca exista pe piata.
  const lastOrder = useMemo(() => {
    const slugs = new Set(bootstrap.storefronts.map((storefront) => storefront.slug));
    return (
      readRememberedOrders(marketKey).find(
        (order) =>
          typeof order.storefrontSlug === "string" &&
          slugs.has(order.storefrontSlug) &&
          Array.isArray(order.lines) &&
          order.lines.length > 0,
      ) ?? null
    );
  }, [bootstrap.storefronts, marketKey]);

  const ctx = useMemo<BlockContext>(
    () => ({
      requestAddress,
      addressLabel: session.address === null ? null : formatAddress(session.address),
      openStorefront: (slug: string) => {
        const storefront = bootstrap.storefronts.find((item) => item.slug === slug);
        selectStorefront(slug, storefront?.name ?? null);
        navigate(`/vitrina/${encodeURIComponent(slug)}`);
      },
      t,
    }),
    [requestAddress, session.address, selectStorefront, navigate, t, bootstrap.storefronts],
  );

  const reorder = (): void => {
    if (lastOrder === null || typeof lastOrder.storefrontSlug !== "string") return;
    const lines = cartLinesFromRemembered(lastOrder.lines ?? []);
    if (lines.length === 0) return;
    const storefront = bootstrap.storefronts.find((item) => item.slug === lastOrder.storefrontSlug) ?? null;
    restoreOrder(lastOrder.storefrontSlug, storefront?.name ?? lastOrder.storefrontName ?? null, lines);
    toast({
      key: "reorder",
      title: "Produsele sunt din nou în coș",
      description: "Le verificăm la adresa ta înainte de finalizare.",
    });
    navigate("/cos");
  };

  return (
    <>
      {lastOrder === null ? null : (
        <section className="mb-6 flex flex-col gap-3 rounded-card border border-line bg-surface p-4 sm:flex-row sm:items-center" aria-labelledby="comanda-din-nou-titlu">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
            <IconRepeat size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="comanda-din-nou-titlu" className="text-base font-bold text-ink">
              Comandă din nou
            </h2>
            <p className="da-clamp-1 text-sm text-ink-muted">
              {[
                lastOrder.storefrontName ?? lastOrder.sellerName ?? null,
                (lastOrder.lines ?? []).map((line) => `${line.qty}× ${line.name}`).join(", "),
              ]
                .filter((part): part is string => part !== null && part.length > 0)
                .join(" · ")}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {typeof lastOrder.total === "number" ? <Price amount={lastOrder.total} className="text-sm font-bold text-ink" /> : null}
            <Button size="sm" onClick={reorder} trailing={<IconChevronRight size={16} />}>
              Repetă comanda
            </Button>
          </div>
        </section>
      )}

      <BlockStack
        blocks={blocks}
        ctx={ctx}
        // Pagina de start nu are un titlu propriu de afisat, dar documentul are
        // nevoie de unul: o vitrina compusa doar din bara de adresa si lista de
        // restaurante e o alegere legitima, un document fara `h1` nu e.
        fallbackHeading={config.identity.name || bootstrap.marketName}
      />

    </>
  );
}
